"use client";

import { usePathname } from "next/navigation";
import { useEffect } from "react";

import { useCookieConsent } from "@/lib/legal/cookie-consent";

import { clearIdentityCookies, ensureIdentityCookies } from "./identity-cookies-client";
import { useAnalyticsListId } from "./list-context";

/** At most one renewal a minute from the pointer and the keyboard: the session is 30 minutes long. */
const RENEWAL_THROTTLE_MS = 60_000;

/**
 * Keeps the identity cookies (`identity-cookies.ts`) in step with the visitor's answer.
 *
 * ```
 * consent === "aceito"   → creates what is missing on every page and every list, and renews the
 *                          session on navigation and on activity
 * consent === "recusado" → deletes the three (a refusal after an accept)
 * consent === "pendente" → nothing: neither creates nor deletes
 * ```
 *
 * "Pending" must not delete. It is what the server snapshot says, so it is the first thing every
 * document sees while hydrating — for someone who accepted last week too — and deleting then would
 * replace their identity on every page load. A cookie can only exist for an answer that was a yes,
 * so a pending visitor has none to delete anyway.
 *
 * Mounted once in the root layout; renders nothing.
 */
export function AnalyticsIdentity() {
  const { consent } = useCookieConsent();
  const pathname = usePathname();
  const listId = useAnalyticsListId();

  useEffect(() => {
    if (consent === "aceito") ensureIdentityCookies({ listId });
    else if (consent === "recusado") clearIdentityCookies();
  }, [consent, pathname, listId]);

  useEffect(() => {
    if (consent !== "aceito") return;

    let lastRenewal = Date.now();
    const renew = () => {
      const now = Date.now();
      if (now - lastRenewal < RENEWAL_THROTTLE_MS) return;

      lastRenewal = now;
      ensureIdentityCookies({ listId });
    };

    window.addEventListener("pointerdown", renew, { passive: true });
    window.addEventListener("keydown", renew, { passive: true });

    return () => {
      window.removeEventListener("pointerdown", renew);
      window.removeEventListener("keydown", renew);
    };
  }, [consent, listId]);

  return null;
}
